// Loop Price Sync for ATC Button
class LoopPriceSync {
  constructor() {
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setupEventListeners());
    } else {
      this.setupEventListeners();
    }
  }

  setupEventListeners() {
    // Listen for Loop widget changes
    this.observeLoopWidgetChanges();
    
    // Listen for radio button selections
    this.listenForRadioChanges();
    
    // Listen for selling plan dropdown changes
    this.listenForSellingPlanChanges();
    
    // Setup sticky button to trigger main button
    this.setupStickyButtonTrigger();
    
    // Initial price sync
    setTimeout(() => this.syncPrice(), 100);
  }

  observeLoopWidgetChanges() {
    const loopContainer = document.querySelector('[id*="loop-widget-container"]');
    if (!loopContainer) return;

    // Observe changes to the loop widget container
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          this.syncPrice();
        }
      });
    });

    observer.observe(loopContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });
  }

  listenForRadioChanges() {
    // Listen for purchase option radio button changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('.loop-widget-purchase-option-radio input') || 
          event.target.closest('.loop-widget-purchase-option-radio')) {
        setTimeout(() => this.syncPrice(), 10);
      }
    });

    // Listen for clicks on radio buttons and labels
    document.addEventListener('click', (event) => {
      if (event.target.matches('.loop-widget-purchase-option-radio') ||
          event.target.closest('.loop-widget-purchase-option-radio') ||
          event.target.matches('.loop-widget-purchase-option-label')) {
        setTimeout(() => this.syncPrice(), 10);
      }
    });
  }

  listenForSellingPlanChanges() {
    // Listen for selling plan dropdown changes
    document.addEventListener('change', (event) => {
      if (event.target.matches('.loop-widget-sp-selector')) {
        setTimeout(() => this.syncPrice(), 10);
      }
    });
  }

  setupStickyButtonTrigger() {
    // Make sticky button trigger the main add-to-cart button
    document.addEventListener('click', (event) => {
      if (event.target.matches('[data-trigger-main-atc]') || 
          event.target.closest('[data-trigger-main-atc]')) {
        event.preventDefault();
        
        // Find the main add-to-cart button
        const mainAtcButton = document.querySelector('.main-product .add-to-cart[type="submit"]') ||
                             document.querySelector('form[data-type="add-to-cart-form"] button[name="add"]') ||
                             document.querySelector('.product-form button[name="add"]');
        
        if (mainAtcButton) {
          // Trigger the main button click
          mainAtcButton.click();
          console.log('Sticky button triggered main ATC button');
        } else {
          console.warn('Main add-to-cart button not found');
        }
      }
    });
  }

  getSelectedPrice() {
    // Find the selected purchase option
    const selectedOption = document.querySelector('.loop-widget-purchase-option-selected') ||
                          document.querySelector('.loop-widget-purchase-option:has(.loop-widget-purchase-option-radio input:checked)');
    
    if (!selectedOption) {
      // Fallback to the first option if none is explicitly selected
      const firstOption = document.querySelector('.loop-widget-purchase-option');
      if (firstOption) {
        return this.extractPriceFromOption(firstOption);
      }
      return null;
    }

    return this.extractPriceFromOption(selectedOption);
  }

  extractPriceFromOption(option) {
    // Get the price container from the selected option
    const priceContainer = option.querySelector('.loop-widget-purchase-option-price-container');
    if (!priceContainer) return null;

    // Get both sale price and compare-at price
    const salePriceElement = priceContainer.querySelector('.loop-widget-purchase-option-price');
    const compareAtPriceElement = priceContainer.querySelector('.loop-widget-purchase-option-compare-at-price');
    
    if (!salePriceElement) return null;

    const salePriceText = salePriceElement.textContent.trim();
    const compareAtPriceText = compareAtPriceElement ? compareAtPriceElement.textContent.trim() : null;
    
    // Extract numeric value from sale price string (e.g., "$84.96" -> 8496)
    const salePriceMatch = salePriceText.match(/[\d,]+\.?\d*/);
    if (!salePriceMatch) return null;

    const cleanSalePrice = salePriceMatch[0].replace(/,/g, '');
    const saleCents = Math.round(parseFloat(cleanSalePrice) * 100);
    
    // Extract compare-at price if it exists
    let compareAtCents = null;
    if (compareAtPriceText) {
      const compareAtPriceMatch = compareAtPriceText.match(/[\d,]+\.?\d*/);
      if (compareAtPriceMatch) {
        const cleanCompareAtPrice = compareAtPriceMatch[0].replace(/,/g, '');
        compareAtCents = Math.round(parseFloat(cleanCompareAtPrice) * 100);
      }
    }
    
    return {
      cents: saleCents,
      formatted: salePriceText,
      compareAtCents: compareAtCents,
      compareAtFormatted: compareAtPriceText,
      hasCompareAtPrice: !!compareAtPriceText
    };
  }

  syncPrice() {
    const selectedPrice = this.getSelectedPrice();
    if (!selectedPrice) return;

    // Update all ATC buttons with the selected Loop price
    this.updateATCButtons(selectedPrice);
  }

  updateATCButtons(priceData) {
    // Update buttons for specific templates (mnm-australia, etc.)
    const specificButtons = document.querySelectorAll('.mnm-australia-btn .price-display .f-price-item--regular');
    specificButtons.forEach(priceElement => {
      priceElement.textContent = priceData.formatted;
      priceElement.dataset.loopPrice = priceData.formatted;
    });

    // Also update any other price displays in ATC buttons
    const allPriceDisplays = document.querySelectorAll('.add-to-cart .price-display .f-price-item--regular');
    allPriceDisplays.forEach(priceElement => {
      priceElement.textContent = priceData.formatted;
      priceElement.dataset.loopPrice = priceData.formatted;
    });

    // Update sticky ATC price elements
    if (priceData.hasCompareAtPrice) {
      // Handle sale scenario - show sale price in sale element and compare price in regular element
      const stickySalePrices = document.querySelectorAll('.prod__sticky-atc .f-price-item--sale');
      stickySalePrices.forEach(priceElement => {
        priceElement.textContent = priceData.formatted;
        priceElement.dataset.loopPrice = priceData.formatted;
      });

      const stickyComparePrices = document.querySelectorAll('.prod__sticky-atc .f-price-item--regular');
      stickyComparePrices.forEach(priceElement => {
        priceElement.textContent = priceData.compareAtFormatted;
        priceElement.dataset.loopComparePrice = priceData.compareAtFormatted;
      });

      // Update the specific prod__price element (this is typically the sale price)
      const stickyPriceElements = document.querySelectorAll('.prod__sticky-atc .prod__price.text-color-regular-price');
      stickyPriceElements.forEach(priceElement => {
        priceElement.textContent = priceData.formatted;
        priceElement.dataset.loopPrice = priceData.formatted;
      });

      // Show the sticky ATC in sale mode by adding sale class to the price container
      const stickyPriceContainers = document.querySelectorAll('.prod__sticky-atc .f-price');
      stickyPriceContainers.forEach(container => {
        container.classList.add('f-price--on-sale');
      });
    } else {
      // Handle regular pricing - no sale
      const stickyRegularPrices = document.querySelectorAll('.prod__sticky-atc .f-price-item--regular');
      stickyRegularPrices.forEach(priceElement => {
        priceElement.textContent = priceData.formatted;
        priceElement.dataset.loopPrice = priceData.formatted;
      });

      const stickyPriceElements = document.querySelectorAll('.prod__sticky-atc .prod__price.text-color-regular-price');
      stickyPriceElements.forEach(priceElement => {
        priceElement.textContent = priceData.formatted;
        priceElement.dataset.loopPrice = priceData.formatted;
      });

      // Remove sale mode from sticky ATC
      const stickyPriceContainers = document.querySelectorAll('.prod__sticky-atc .f-price');
      stickyPriceContainers.forEach(container => {
        container.classList.remove('f-price--on-sale');
      });
    }

    const buttons = document.querySelectorAll('.add-to-cart[data-product-id]');
    buttons.forEach(button => {
      const priceDisplay = button.querySelector('.price-display');
      if (priceDisplay) {
        priceDisplay.dataset.loopPrice = priceData.cents;
      }
    });

    // Trigger custom event for other scripts that might need to know about price changes
    document.dispatchEvent(new CustomEvent('loop:priceChanged', {
      detail: {
        price: priceData.cents,
        formatted: priceData.formatted
      }
    }));
  }

  // Method to reset to default prices if Loop widget is removed or disabled
  resetToDefaultPrices() {
    const allPriceDisplays = document.querySelectorAll('.add-to-cart .price-display .f-price-item--regular');
    allPriceDisplays.forEach(priceElement => {
      const defaultPrice = priceElement.dataset.defaultPrice || priceElement.dataset.variantPrice;
      if (defaultPrice) {
        priceElement.textContent = defaultPrice;
        delete priceElement.dataset.loopPrice;
      }
    });

    // Reset sticky ATC price elements
    const stickyPriceElements = document.querySelectorAll('.prod__sticky-atc .prod__price.text-color-regular-price, .prod__sticky-atc .f-price-item--regular, .prod__sticky-atc .f-price-item--sale');
    stickyPriceElements.forEach(priceElement => {
      const defaultPrice = priceElement.dataset.defaultPrice || priceElement.dataset.variantPrice;
      if (defaultPrice) {
        priceElement.textContent = defaultPrice;
        delete priceElement.dataset.loopPrice;
      }
    });
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new LoopPriceSync();
  });
} else {
  new LoopPriceSync();
}

// Export for potential use by other scripts
window.LoopPriceSync = LoopPriceSync;
